import React from 'react';
import { Plus, ChevronLeft, Trash2 } from 'lucide-react';
import type { ApiBoard } from 'services/kanban';
import styles from './BoardPicker.module.scss';

export interface BoardPickerProps {
  companyId: string | null;
  boards: ApiBoard[];
  boardsLoading: boolean;
  boardsError: string | null;
  creatingBoard: boolean;
  onSelectBoard: (boardId: string) => void;
  onCreateBoard: () => void;
  onDeleteBoard: (board: ApiBoard) => void;
}

export default function BoardPicker({
  companyId,
  boards,
  boardsLoading,
  boardsError,
  creatingBoard,
  onSelectBoard,
  onCreateBoard,
  onDeleteBoard,
}: BoardPickerProps) {
  if (!companyId) {
    return (
      <div className={styles.pickerPage}>
        <div className={styles.pickerHeader}>
          <h1 className={styles.pickerTitle}>Kanban</h1>
          <p className={styles.pickerSubtitle}>
            Select a company to view boards.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.pickerPage}>
      <div className={styles.pickerHeader}>
        <h1 className={styles.pickerTitle}>Kanban</h1>
        <p className={styles.pickerSubtitle}>Choose a board or create one</p>
      </div>
      <div className={styles.pickerContent}>
        {boardsLoading ? (
          <p className={styles.pickerMessage}>Loading boards...</p>
        ) : boardsError ? (
          <p className={styles.pickerError}>{boardsError}</p>
        ) : (
          <>
            {boards.length === 0 ? (
              <p className={styles.pickerMessage}>No boards available</p>
            ) : (
              <ul className={styles.pickerList}>
                {boards.map((board) => (
                  <li key={board.id} className={styles.pickerRow}>
                    <button
                      type="button"
                      className={styles.pickerItem}
                      onClick={() => onSelectBoard(board.id)}
                      disabled={creatingBoard}
                    >
                      <span className={styles.pickerItemTitle}>
                        {board.title}
                      </span>
                      <ChevronLeft
                        size={18}
                        style={{ transform: 'rotate(-90deg)' }}
                        aria-hidden
                      />
                    </button>
                    <button
                      type="button"
                      className={styles.pickerDeleteBtn}
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteBoard(board);
                      }}
                      aria-label={`Delete board ${board.title}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <button
              type="button"
              className={styles.pickerCreateBtn}
              onClick={() => void onCreateBoard()}
              disabled={boardsLoading || creatingBoard}
            >
              <Plus size={18} style={{ marginRight: 8 }} />
              {creatingBoard ? 'Creating board...' : 'Create board'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
